import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook to manage an OTP (One-Time Password) countdown timer.
 *
 * Features:
 * - Can start a timer directly with seconds.
 * - Can start a timer using an expiry timestamp (from backend).
 * - Provides formatted remaining time (MM:SS).
 * - Tracks whether the timer has expired.
 *
 * @returns {Object} API
 * @returns {number} API.remaining - Remaining seconds.
 * @returns {boolean} API.expired - Whether the timer has expired.
 * @returns {string} API.formatted - Formatted remaining time (MM:SS).
 * @returns {(seconds: number) => void} API.start - Start timer with seconds.
 * @returns {(expirytime: string) => void} API.startFromExpiry - Start timer using expiry string (e.g. "2025-09-04T12:13:09.000Z").
 *
 * @example
 * const { remaining, expired, formatted, start, startFromExpiry } = useOtpTimer();
 *
 * // Start with 30 seconds
 * useEffect(() => {
 *   start(30);
 * }, []);
 *
 * // OR start from backend expiry timestamp
 * useEffect(() => {
 *   startFromExpiry("2025-09-04T12:13:09.000Z");
 * }, []);
 *
 * return (
 *   <div>
 *     <p>Time left: {formatted}</p>
 *     {expired && <p>OTP expired!</p>}
 *   </div>
 * );
 */

// helper to format time
const formatTime = (seconds) => {
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

export const useOtpTimer = () => {
    const [remaining, setRemaining] = useState(0);
    const [expired, setExpired] = useState(false);
    const intervalRef = useRef(null);

    // Start timer by passing expiry string from backend
    const startFromExpiry = (expirytime) => {
        if (!expirytime) {
            setRemaining(0);
            setExpired(true);
            return;
        }

        // Handle backend expiry string
        const expiryTime = new Date(expirytime.replace('Z', '')).getTime();
        const now = Date.now();
        const seconds = Math.max(0, Math.floor((expiryTime - now) / 1000));

        start(seconds);
    };

    // Generic start (seconds)
    const start = (seconds) => {
        clearInterval(intervalRef.current);

        if (!seconds || seconds <= 0) {
            setRemaining(0);
            setExpired(true);
            return;
        }

        setRemaining(seconds);
        setExpired(false);

        intervalRef.current = setInterval(() => {
            setRemaining((prev) => {
                if (prev <= 1) {
                    clearInterval(intervalRef.current);
                    setExpired(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => clearInterval(intervalRef.current);
    }, []);

    return { remaining, expired, formatted: formatTime(remaining), start, startFromExpiry };
};
